import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
  Card,
} from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { feedbackHrOneOnOneApiService } from '../../services/apiServices';
import { FeedbackApplicationResponse, FeedbackApplicationStatus } from '../../api-client';
import { getFeedbackApplicationStatusLabel } from '../utils/statusConverters';
import { SmartPagination } from '../../components/SmartPagination';
import FeedbackCreateForm from '../../components/FeedbackCreateForm';

const MyFeedbackHrOneOnOnePage: React.FC = () => {
  const [applications, setApplications] = useState<FeedbackApplicationResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [createSuccess, setCreateSuccess] = useState<boolean>(false);

  const statusFilter = searchParams.get('status') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await feedbackHrOneOnOneApiService.getMyApplications(
        statusFilter || undefined,
        limit,
        (currentPage - 1) * limit
      );
      setApplications(response.data || []);
    } catch (err: any) {
      console.error('Ошибка при загрузке моих заявок HR 1-on-1:', err);
      setError(err.message || 'Ошибка при загрузке заявок');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currentPage, limit]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleStatusChange = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (value) {
          params.set('status', value);
        } else {
          params.delete('status');
        }
        params.set('page', '1');
        return params;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', newPage.toString());
        return params;
      });
    },
    [setSearchParams]
  );

  const handleCreateSubmit = async (description: string) => {
    await feedbackHrOneOnOneApiService.createMyApplication(description || undefined);
    setShowCreateForm(false);
    setCreateSuccess(true);
    loadApplications();
    setTimeout(() => setCreateSuccess(false), 3000);
  };

  const handleResetFilters = useCallback(() => {
    setSearchParams({}, { replace: false });
  }, [setSearchParams]);

  const getStatusBadgeVariant = (status: FeedbackApplicationStatus) => {
    switch (status) {
      case FeedbackApplicationStatus.PENDING:
        return 'warning';
      case FeedbackApplicationStatus.SCHEDULED:
        return 'info';
      case FeedbackApplicationStatus.COMPLETED:
        return 'success';
      case FeedbackApplicationStatus.CANCELED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Мои заявки на HR 1-on-1</h2>
            <Button
              variant={showCreateForm ? 'outline-secondary' : 'primary'}
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? 'Скрыть форму' : 'Создать заявку'}
            </Button>
          </div>

          {showCreateForm && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Новая заявка</h5>
                <FeedbackCreateForm
                  onSubmit={handleCreateSubmit}
                  onCancel={() => setShowCreateForm(false)}
                />
              </Card.Body>
            </Card>
          )}

          {createSuccess && <Alert variant="success">Заявка успешно создана!</Alert>}

          {/* Фильтры */}
          <div className="row mb-4">
            <div className="col-md-3 mb-2">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">Все</option>
                <option value="pending">Ожидает обработки</option>
                <option value="scheduled">Запланировано</option>
                <option value="completed">Завершено</option>
                <option value="canceled">Отменено</option>
              </Form.Select>
            </div>
            <div className="col-md-2 mb-2 d-flex align-items-end">
              <Button variant="outline-secondary" className="w-100" onClick={handleResetFilters}>
                Сбросить
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Загрузка заявок...</p>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Статус</th>
                    <th style={{ maxWidth: '400px' }}>Описание</th>
                    <th>Дата создания</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {applications && applications.length > 0 ? (
                    applications.map((app) => (
                      <tr key={app.id}>
                        <td>
                          <Badge bg={getStatusBadgeVariant(app.status)}>
                            {getFeedbackApplicationStatusLabel(app.status)}
                          </Badge>
                        </td>
                        <td style={{ wordBreak: 'break-word', maxWidth: '400px' }}>
                          {app.description || '—'}
                        </td>
                        <td>{new Date(app.created_at).toLocaleDateString()}</td>
                        <td>
                          <Link to={`/feedback/hr-one-on-one/my/${app.id}`}>
                            <Button variant="outline-primary" size="sm">
                              Подробнее
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        У вас пока нет заявок
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <SmartPagination
                currentPage={currentPage}
                limit={limit}
                currentItemsCount={applications.length}
                onPageChange={handlePageChange}
                siblingCount={1}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default MyFeedbackHrOneOnOnePage;
